import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import api from "../../../utils/api";
import { formatMoney } from "../../../utils/helpers";

function todayLocalISO() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function GenerateFromTemplateDialog({ open, onClose, onCommitted }) {
    const [templates, setTemplates] = useState([]);
    const [templateId, setTemplateId] = useState("");
    const [effectiveDate, setEffectiveDate] = useState(todayLocalISO());
    const [rows, setRows] = useState([]);
    const [expenseTypes, setExpenseTypes] = useState([]);

    const totalAmount = useMemo(
        () => rows.reduce((acc, r) => acc + (Number(r.amount) || 0), 0),
        [rows]
    );
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const loadTemplates = useCallback(async () => {
        try {
            const res = await api.get("admin/finance-expense-templates");
            setTemplates(res.data?.data || []);
        } catch (err) {
            console.error(err);
        }
    }, []);

    const loadExpenseTypes = useCallback(async () => {
        try {
            const res = await api.get("admin/expense-types/list");
            setExpenseTypes(res.data?.data || []);
        } catch (err) {
            console.error("Failed to load expense types", err);
        }
    }, []);

    useEffect(() => {
        if (!open) {
            setTemplateId("");
            setRows([]);
            setError("");
            setEffectiveDate(todayLocalISO());
            return;
        }
        loadTemplates();
        loadExpenseTypes();
    }, [open, loadTemplates, loadExpenseTypes]);

    const loadPreview = async () => {
        if (!templateId) {
            setError("Select a template.");
            return;
        }
        setLoading(true);
        setError("");
        try {
            const res = await api.post(`admin/finance-expense-templates/${templateId}/preview`, {
                effective_date: effectiveDate || null,
            });
            const payload = res.data?.data || {};
            const lines = payload.lines || [];
            setRows(
                lines.map((ln) => ({
                    line_id: ln.line_id,
                    direction: ln.direction,
                    expense_type_id: ln.expense_type_id || ln.expense_type?.id || null,
                    description: ln.description || "",
                    amount: ln.amount != null ? String(ln.amount) : "",
                }))
            );
        } catch (err) {
            setError(err.response?.data?.message || "Preview failed.");
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    const updateRow = (index, patch) => {
        setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
    };

    const removeRow = (index) => {
        setRows((prev) => prev.filter((_, i) => i !== index));
    };

    const commit = async () => {
        if (!templateId) {
            setError("Select a template.");
            return;
        }
        const linesPayload = rows
            .map((r) => ({
                direction: r.direction,
                expense_type_id: r.direction === "expense" ? (r.expense_type_id || null) : null,
                description: String(r.description || "").trim(),
                amount: Number(r.amount),
            }))
            .filter((r) => r.description && Number.isFinite(r.amount) && r.amount > 0);

        if (!linesPayload.length) {
            setError("Load preview and keep at least one valid line.");
            return;
        }

        setSaving(true);
        setError("");
        try {
            await api.post(`admin/finance-expense-templates/${templateId}/commit`, {
                effective_date: effectiveDate,
                lines: linesPayload,
            });
            onCommitted?.();
            onClose();
        } catch (err) {
            setError(err.response?.data?.message || "Could not create entries.");
        } finally {
            setSaving(false);
        }
    };

    const selectedTemplateName = templates.find((t) => String(t.id) === String(templateId))?.name || "";

    return (
        <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="md" fullWidth>
            <DialogTitle>Generate from template</DialogTitle>
            <DialogContent>
                {error ? <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{error}</Alert> : null}
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <FormControl size="small" fullWidth>
                        <InputLabel id="tpl-select-label">Template</InputLabel>
                        <Select
                            labelId="tpl-select-label"
                            label="Template"
                            value={templateId}
                            onChange={(e) => setTemplateId(e.target.value)}
                        >
                            <MenuItem value="">
                                <em>Select…</em>
                            </MenuItem>
                            {templates.filter((t) => t.is_active !== false).map((t) => (
                                <MenuItem key={t.id} value={String(t.id)}>
                                    {t.name} {t.lines_count != null ? `(${t.lines_count})` : ""}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                        <OutlinedInput
                            size="small"
                            type="date"
                            label="Effective date"
                            value={effectiveDate}
                            onChange={(e) => setEffectiveDate(e.target.value)}
                        />
                        <Button variant="outlined" onClick={loadPreview} disabled={loading || !templateId}>
                            {loading ? "Loading…" : "Load preview"}
                        </Button>
                    </Stack>
                    {selectedTemplateName ? (
                        <Typography variant="body2" color="text.secondary">
                            Template: {selectedTemplateName}
                        </Typography>
                    ) : null}
                    <Typography variant="body2" color="text.secondary">
                        Total: {formatMoney(totalAmount)} • Lines: {rows.length}
                    </Typography>
                    <Box sx={{ maxHeight: 360, overflow: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Direction</TableCell>
                                    <TableCell>Expense type</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Amount</TableCell>
                                    <TableCell align="right"> </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row, index) => {
                                    const selectedType = expenseTypes.find((t) => Number(t.id) === Number(row.expense_type_id)) || null;
                                    return (
                                        <TableRow key={row.line_id ?? index}>
                                            <TableCell sx={{ minWidth: 120 }}>
                                                <Select
                                                    size="small"
                                                    fullWidth
                                                    value={row.direction}
                                                    onChange={(e) => updateRow(index, { direction: e.target.value, expense_type_id: e.target.value === "income" ? null : row.expense_type_id })}
                                                >
                                                    <MenuItem value="expense">Expense</MenuItem>
                                                    <MenuItem value="income">Income</MenuItem>
                                                </Select>
                                            </TableCell>
                                            <TableCell sx={{ minWidth: 180 }}>
                                                {row.direction === "expense" ? (
                                                    <Autocomplete
                                                        size="small"
                                                        options={expenseTypes}
                                                        value={selectedType}
                                                        getOptionLabel={(option) => option?.name || ""}
                                                        isOptionEqualToValue={(option, value) => Number(option?.id) === Number(value?.id)}
                                                        onChange={(_, val) => updateRow(index, { expense_type_id: val?.id || null })}
                                                        renderInput={(params) => <TextField {...params} placeholder="Type" />}
                                                    />
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">—</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <OutlinedInput
                                                    size="small"
                                                    fullWidth
                                                    value={row.description}
                                                    onChange={(e) => updateRow(index, { description: e.target.value })}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ minWidth: 120 }}>
                                                <OutlinedInput
                                                    size="small"
                                                    type="number"
                                                    inputProps={{ min: 0.01, step: 0.01 }}
                                                    value={row.amount}
                                                    onChange={(e) => updateRow(index, { amount: e.target.value })}
                                                    startAdornment={<InputAdornment position="start">֏</InputAdornment>}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton size="small" color="error" onClick={() => removeRow(index)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {!rows.length ? (
                                    <TableRow>
                                        <TableCell colSpan={5}>
                                            <Typography variant="body2" color="text.secondary" align="center">
                                                Load preview to edit amounts.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : null}
                            </TableBody>
                        </Table>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="contained" onClick={commit} disabled={saving || !rows.length}>
                    {saving ? "Saving…" : "Create entries"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
